import { FC, ReactElement, useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { Sidebar, Menu, MenuItem, SubMenu } from "react-pro-sidebar";
import { Box, Divider, Tooltip, Typography, useTheme } from "@mui/material";
import {
  useSidebar,
  useSidebarSelectedMenuTitleContext,
  useTemplateThemeModeContext,
} from "../../hooks";
import { TemplateThemeModeContextType } from "../../context";
import InnoviastaaLogo from "@images/logo/Innoviastaa-logo.png";
import KeyboardDoubleArrowLeftIcon from "@mui/icons-material/KeyboardDoubleArrowLeft";
import KeyboardDoubleArrowRightIcon from "@mui/icons-material/KeyboardDoubleArrowRight";
import "./Sidebar.scss";
import { color } from "../../styles/CommonStyle";
import SidebarContent from "./SidebarContent";
import innovistaaLogoCollapsed from "@images/logo/innovistaaLogoCollapsed.svg";
import { useSelector } from "react-redux";
import { RootState } from "@redux/store/store";
import moment from "moment";

const SideBar: FC = (): ReactElement => {
  const theme = useTheme();
  const navigate = useNavigate();
  const location = useLocation();
  const { toggle, menuItemStyles } = useSidebar();
  const { setMenuTitle } = useSidebarSelectedMenuTitleContext();
  const { isDark } =
    useTemplateThemeModeContext() as TemplateThemeModeContextType;
  const [activeMenu, setActiveMenu] = useState<string | null>(null);
  const [collapsed, setCollapsed] = useState<boolean>(false);
  const sidebarItems = SidebarContent();
  const dateFormatDetails = useSelector((state: RootState) => state.currencydateFormatDetails.currencydateFormatDetails);
  const isSuperAdmin = useSelector((state: RootState) => state.auth.user.roleId === 1);
  const expiryDate = useSelector((state: RootState) => state.auth.user.endDate);

  const extractTime = (dateString: string) => {
      return moment(dateString).format('MMM D, YYYY');
    };

  const handleMenuItemClick = (label: string, path: string) => {
    setMenuTitle(label);
    setActiveMenu(label);
    navigate(path, { replace: true });
  };

  useEffect(() => {
    const currentPath = location.pathname;
    const foundItem = sidebarItems.find(
      (menuItem: any) => {
        return menuItem.path === currentPath || (menuItem.submenu && menuItem.submenu.find((subItem: any) => {
          return subItem.path === currentPath ? subItem : null;
        }))
      }
    );
    if (foundItem?.submenu) {
      foundItem.submenu.map((submenuItem: any) => {
        if (submenuItem.path === currentPath) {
          setMenuTitle(submenuItem?.label);
          setActiveMenu(submenuItem?.label);
        }
        return { setMenuTitle, setActiveMenu }
      })
    } else if (foundItem) {
      setMenuTitle(foundItem?.label);
      setActiveMenu(foundItem?.label);
    }
  }, [location.pathname, setMenuTitle]);

  return (
    <Sidebar
      rtl={false}
      breakPoint="sm"
      collapsed={collapsed}
      backgroundColor={
        isDark ? theme.palette.primary.dark : theme.palette.primary.light
      }
      rootStyles={{
        color: isDark
          ? theme.palette.secondary.dark
          : theme.palette.secondary.light,
      }}
      className={`sidebarMain ${collapsed ? "collapsed" : ""}`}
      width="256px"
    >
      <Menu>
        <MenuItem
          style={{
            textAlign: "center",
            height: "auto",
            marginTop: 0,
            backgroundColor: isDark
              ? theme.palette.info.dark
              : theme.palette.info.light,
          }}
        >
          <div className="sidebarLogo">
            {!collapsed ? (
              <img
                src={InnoviastaaLogo}
                alt="Innoviastaa"
                width={150}
                className="imgContainer"
              />
            ) : (
              <img
                src={innovistaaLogoCollapsed}
                alt="Innoviastaa logo"
                width={25}
                className="imgContainerCollapse"
              />
            )}
          </div>
        </MenuItem>
      </Menu>
      {!collapsed && expiryDate && isSuperAdmin && ( <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            marginTop: "10px",
            marginBottom: "10px"
          }}
        >
          <Typography sx={{ color: '#ffffff !important', fontSize: "12px", fontWeight: 600}}>
            Your license will expire on {extractTime(expiryDate)}
          </Typography>
        </Box>)}
      <Menu menuItemStyles={menuItemStyles}>
        {sidebarItems.map((menuItem: Record<string, any>) => {
          return menuItem.submenu ? (
            menuItem.isVisible && (
              <SubMenu
                key={menuItem.label}
                icon={menuItem.icon}
                label={menuItem.label}
                className={`sidebarItems parentMenu ${collapsed ? "" : "expandedCustomSubMenu"} ${activeMenu === menuItem.label ? "active" : ""
                  }`}
                style={{ fontSize: "14px", height: '35px' }}
                title={collapsed ? menuItem.label : ""}
              >
                {menuItem.submenu.map((subItem: Record<string, any>) => (

                  subItem.isVisible &&
                  (<Tooltip title={subItem.label} placement={'right'}>

                    <MenuItem
                      key={subItem.label}
                      icon={subItem.icon}
                      onClick={() =>
                        handleMenuItemClick(subItem.label, subItem.path)
                      }

                      className={`sidebarItems parentMenu ${activeMenu === subItem.label ? "active" : ""
                        }`}
                    >

                      <Typography component={"div"}>
                        <Typography
                          variant="body2"
                          sx={{
                            fontSize: !collapsed ? "12px" : "14px",
                          }}
                        >
                          {subItem.label}
                        </Typography>
                      </Typography>
                    </MenuItem>
                  </Tooltip>)
                ))}
              </SubMenu>
            )
          ) : (
            menuItem.isVisible && (
              <MenuItem
                key={menuItem.label}
                onClick={() => handleMenuItemClick(menuItem.label, menuItem.path)}

                className={`sidebarItems parentMenu ${activeMenu === menuItem.label ? "active" : ""
                  }`}
              >
                {collapsed ? (
                  <div style={{ marginLeft: "10px" }}>
                    <Tooltip title={menuItem.label} placement={'right'}>
                      {menuItem.icon}
                    </Tooltip>
                  </div>
                ) : (
                  <div style={{ display: "flex" }}>
                    {menuItem.icon}
                    <Typography
                      variant="body2"
                      sx={{
                        fontSize: "14px",
                        marginLeft: "10px",
                      }}
                    >
                      {menuItem.label}
                    </Typography>
                  </div>
                )}
              </MenuItem>
            )
          );
        })}
      </Menu>
      {/* Toggle button */}
      <Box
        sx={{
          flexGrow: 1,
          display: "flex",
          flexDirection: "column",
          justifyContent: "flex-end",
        }}
      >
        <Divider sx={{ width: "100%", color: `${color.white}` }} />
        <Menu>
          <MenuItem
            id="sidebarMnuHeader"
            style={{
              textAlign: "right",
              height: 48,
              marginTop: 0,
              backgroundColor: isDark
                ? theme.palette.info.dark
                : theme.palette.info.light,
            }}
          >
            {!collapsed ? (
              <KeyboardDoubleArrowLeftIcon
                sx={{ color: `${color.slate}` }}
                fontSize="medium"
                onClick={() => {
                  toggle();
                  setCollapsed(!collapsed);
                }}
              />
            ) : (
              <KeyboardDoubleArrowRightIcon
                sx={{ color: `${color.slate}` }}
                fontSize="medium"
                onClick={() => {
                  toggle();
                  setCollapsed(!collapsed);
                }}
              />
            )}
          </MenuItem>
        </Menu>
      </Box>
    </Sidebar>
  );
};

export default SideBar;
